/**
 * DCF mail, the pieces both surfaces share (UM-R5, 2026-10-02): one message
 * opened up -- what DCF said, who sent it, its attachments, the original --
 * and Mark handled.
 *
 * Opening the original or an attachment asks the server for a five-minute
 * link, which the server records before it signs. A NOCA filed on the case is
 * opened through the case's own document route, audited the same way as any
 * document. Nothing here records a NOCA review: a document is evidence, a
 * review is a person (UM-R3) -- "Record NOCA received" stays on the case.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  umedApi, dcfMailApi, shortDateTime, DCF_MAIL_CLASS_LABEL, DCF_MAIL_MATCH_LABEL, DCF_MAIL_ATTACH_LABEL,
} from './umedApi';
import type { DcfMailItem, DcfMailDetail, DcfMailAttachment } from './umedApi';

const CANNOT_REACH = 'Could not reach the server.';

function failure(r: { error?: string; message?: string; code?: string }): string {
  if (r.code === 'NOT_FOUND') return 'This message is not in your scope, or no longer exists.';
  if (r.code === 'NOT_IN_STORAGE') return 'This message was read from a local directory; there is no stored original to open.';
  return r.error || r.message || 'The server refused that.';
}

/** "CounselEAR 123456 · J. D. · Building", or why there is no resident. */
export function residentLine(m: DcfMailItem): string {
  if (m.match_outcome !== 'MATCHED') return `${DCF_MAIL_MATCH_LABEL[m.match_outcome]}${m.to_address ? ` · ${m.to_address}` : ''}`;
  const who = [m.counselear_id ? `CounselEAR ${m.counselear_id}` : 'No CounselEAR id', m.initials].filter(Boolean).join(' · ');
  return `${who}${m.facility_name ? ` · ${m.facility_name}` : ''}`;
}

/** The class, and for a NOCA what happened to its file. */
export function classLine(m: DcfMailItem): string {
  const label = DCF_MAIL_CLASS_LABEL[m.classification] || m.classification;
  if (m.classification === 'UNREADABLE') return `${label}${m.error === 'TOO_LARGE' ? ' (over 25 MB)' : ''}`;
  const attach = DCF_MAIL_ATTACH_LABEL[m.attach_outcome];
  return attach ? `${label} · ${attach}` : label;
}

async function openUrl(get: () => Promise<{ success: boolean; data?: { url: string }; error?: string; code?: string; message?: string }>, setErr: (s: string | null) => void) {
  setErr(null);
  try {
    const r = await get();
    if (!r.success || !r.data) { setErr(failure(r)); return; }
    window.open(r.data.url, '_blank', 'noopener,noreferrer');
  } catch { setErr(CANNOT_REACH); }
}

function AttachmentRow({ mailId, caseId, a, canOpen }: { mailId: string; caseId: string | null; a: DcfMailAttachment; canOpen: boolean }) {
  const [err, setErr] = useState<string | null>(null);
  const kind = a.content_type ? a.content_type.replace(/^application\//, '').replace(/^image\//, 'image ').toUpperCase() : 'unknown type';
  const why = !a.accepted ? (a.reason === 'VIRUS_VERDICT' ? 'held: the virus scan failed' : 'not a PDF or image, not kept') : null;
  // A NOCA filed on the case opens through the case's document route; anything else through the mail's own.
  const open = () => openUrl(() => (a.document_id && caseId ? umedApi.documentUrl(caseId, a.document_id) : dcfMailApi.attachmentUrl(mailId, a.index)), setErr);
  return (
    <li>
      <span>{a.filename || `Attachment ${a.index + 1}`} · {kind} · {Math.max(1, Math.round(a.size / 1024))} KB</span>
      {a.document_id && <span className="um-quiet"> · {a.deduplicated ? 'already on file as a NOCA' : 'filed as a NOCA'}</span>}
      {why && <span className="um-quiet"> · {why}</span>}
      {canOpen && a.openable && <>{' '}<button type="button" className="um-link" onClick={open}>Open</button></>}
      {err && <span className="um-notice stop" role="alert"> {err}</span>}
    </li>
  );
}

function MarkHandled({ mailId, onDone }: { mailId: string; onDone: (msg: string) => void | Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <form className="um-form" aria-label="Mark this message handled" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await dcfMailApi.markHandled(mailId, reason.trim() || null);
        if (!r.success || !r.data) { setErr(failure(r)); return; }
        await onDone(r.data.replayed ? 'Someone had already marked this handled.' : 'Marked handled.');
      } catch { setErr(`${CANNOT_REACH} Nothing was recorded.`); } finally { setBusy(false); }
    }}>
      <label><span>What was done (optional)</span><input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} aria-label="What was done (optional)" /></label>
      <div className="btns"><button type="submit" className="um-open" disabled={busy}>{busy ? 'Recording…' : 'Mark handled'}</button></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** One message, opened. Loads its own detail. */
export function DcfMailMessage({ item, canOpen, canMark, onChanged }: {
  item: DcfMailItem; canOpen: boolean; canMark: boolean; onChanged: (msg: string) => void | Promise<void>;
}) {
  const [d, setD] = useState<DcfMailDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openErr, setOpenErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await dcfMailApi.detail(item.id);
      if (r.success && r.data) setD(r.data); else setErr(failure(r));
    } catch { setErr(CANNOT_REACH); }
  }, [item.id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <p className="um-notice stop" role="alert">{err}</p>;
  if (!d) return <p className="um-quiet" role="status">Loading the message…</p>;
  return (
    <div className="um-dcfmail-message" aria-label="DCF mail message">
      <dl className="um-kv">
        <dt>Subject</dt><dd>{d.subject || <span className="um-quiet">No subject</span>}</dd>
        <dt>From</dt><dd>{d.from_address || <span className="um-quiet">Not stated</span>}</dd>
        <dt>To</dt><dd>{d.to_address || <span className="um-quiet">Not stated</span>}</dd>
        <dt>Received</dt><dd>{shortDateTime(d.received_at)}</dd>
        <dt>What it is</dt><dd>{classLine(d)}</dd>
      </dl>
      {d.text_excerpt
        ? <p className="um-text um-dcfmail-excerpt">{d.text_excerpt}</p>
        : <p className="um-quiet">No words could be read from this message.</p>}
      {d.attachments.length > 0 && (
        <ul className="um-packets" aria-label="Attachments">
          {d.attachments.map((a) => <AttachmentRow key={a.index} mailId={d.id} caseId={d.case_id} a={a} canOpen={canOpen} />)}
        </ul>
      )}
      {canOpen && d.raw_openable && (
        <p>
          <button type="button" className="um-link" onClick={() => openUrl(() => dcfMailApi.rawUrl(d.id), setOpenErr)}>Open the original (.eml)</button>
          {openErr && <span className="um-notice stop" role="alert"> {openErr}</span>}
        </p>
      )}
      {d.handled
        ? <p className="um-quiet">Handled {shortDateTime(d.handled.at)}{d.handled.by_name ? ` by ${d.handled.by_name}` : ''}{d.handled.reason ? ` · ${d.handled.reason}` : ''}</p>
        : canMark && <MarkHandled mailId={d.id} onDone={async (msg) => { await load(); await onChanged(msg); }} />}
    </div>
  );
}
